import { createClient } from '@supabase/supabase-js';
import { sb as sourceClient } from './supabase.ts';

// List of tables to back up (identical to local D1 backup tables)
export const BACKUP_TABLES = [
  'student_database',
  'student_database_others',
  'termination_table',
  'termination_table_others',
  'teacher_database',
  'button_status',
  'admission_data',
  'admin_teacher',
  'FMHS_exams_names',
  'FMHS_exam_subjects',
  'FMHS_exam_teacher_selection',
  'FMHS_exam_data'
];

// Target backup Supabase project credentials (photography project)
const BACKUP_URL = 'https://vbfckjroisrhplrpqzkd.supabase.co';
const BACKUP_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InZiZmNranJvaXNyaHBscnBxemtkIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjE4NDQzODYsImV4cCI6MjA3NzQyMDM4Nn0.nIbdwysoW2dp59eqPh3M9axjxR74rGDkn8OdZciue4Y';

export const backupClient = createClient(BACKUP_URL, BACKUP_ANON_KEY, {
  auth: {
    persistSession: false,
    autoRefreshToken: false
  }
});

/**
 * Backs up all pending records from the source Supabase project (rtfefxghfbtirfnlbucb)
 * to the target backup Supabase project (vbfckjroisrhplrpqzkd) via bulk upserts.
 */
export async function backupToSupabaseBackup(progressCallback?: (msg: string) => void): Promise<void> {
  const rateLimitMs = 60 * 60 * 1000; // 1 hour cooldown per table
  const now = Date.now();
  let timestamps: Record<string, number> = {};
  
  try {
    const raw = localStorage.getItem('supabase_backup_table_timestamps');
    if (raw) timestamps = JSON.parse(raw);
  } catch (e) {
    console.error('Failed to parse Supabase backup timestamps:', e);
  }

  for (let i = 0; i < BACKUP_TABLES.length; i++) {
    const tableName = BACKUP_TABLES[i];
    const currentNum = i + 1;
    const total = BACKUP_TABLES.length;

    // Check rate limit per table (same cooldown as Cloudflare D1 backup)
    const lastSuccess = timestamps[tableName] || 0;
    if (now - lastSuccess < rateLimitMs) {
      if (progressCallback) {
        progressCallback(`[${currentNum}/${total}] Skipped "${tableName}" (recent).`);
      }
      continue;
    }

    if (progressCallback) {
      progressCallback(`[${currentNum}/${total}] Fetching Supabase "${tableName}"...`);
    }

    try {
      // 1. Fetch all rows from source project
      const sourceRows: any[] = [];
      let page = 0;
      const pageSize = 1000;
      
      while (true) {
        const { data, error } = await sourceClient
          .from(tableName)
          .select('*')
          .range(page * pageSize, (page + 1) * pageSize - 1);
          
        if (error) {
          throw new Error(`Failed to fetch source rows: ${error.message}`);
        }
        if (!data || data.length === 0) {
          break;
        }
        sourceRows.push(...data);
        if (data.length < pageSize) {
          break;
        }
        page++;
      }

      if (sourceRows.length === 0) {
        if (progressCallback) {
          progressCallback(`[${currentNum}/${total}] "${tableName}" is empty.`);
        }
        // Save timestamp even if empty
        timestamps[tableName] = Date.now();
        localStorage.setItem('supabase_backup_table_timestamps', JSON.stringify(timestamps));
        continue;
      }

      if (progressCallback) {
        progressCallback(`[${currentNum}/${total}] Syncing ${sourceRows.length} rows to backup "${tableName}"...`);
      }

      // 2. Upsert rows to target project in chunks of 200
      const chunkSize = 200;
      for (let j = 0; j < sourceRows.length; j += chunkSize) {
        const chunk = sourceRows.slice(j, j + chunkSize);
        const { error: upsertErr } = await backupClient
          .from(tableName)
          .upsert(chunk);
          
        if (upsertErr) {
          throw new Error(`Failed to upsert to backup database: ${upsertErr.message}`);
        }
      }

      if (progressCallback) {
        progressCallback(`[${currentNum}/${total}] "${tableName}" successfully synced (${sourceRows.length} rows).`);
      }

      // Save success timestamp
      timestamps[tableName] = Date.now();
      localStorage.setItem('supabase_backup_table_timestamps', JSON.stringify(timestamps));

    } catch (err: any) {
      const errMsg = `[${currentNum}/${total}] ❌ "${tableName}" sync failed: ${err.message}`;
      if (progressCallback) progressCallback(errMsg);
      console.error(errMsg);
    }
    
    // Tiny delay between tables
    await new Promise(resolve => setTimeout(resolve, 200));
  }
}

/**
 * Triggered on login to run the Supabase-to-Supabase backup automatically.
 */
export async function checkAndAutoBackupToSupabase(): Promise<void> {
  const lastSyncDate = localStorage.getItem('supabase_last_auto_backup_date');
  const todayStr = new Date().toISOString().split('T')[0]; // e.g. "2026-07-15"

  // Only run once a day automatically on login
  if (lastSyncDate === todayStr) {
    console.log('[Supabase Auto Backup] Already ran today. Skipping.');
    return;
  }

  console.log(`[Supabase Auto Backup] Starting automatic backup to photography project for ${todayStr}...`);
  try {
    await backupToSupabaseBackup(msg => {
      console.log(`[Supabase Auto Backup] ${msg}`);
    });
    localStorage.setItem('supabase_last_auto_backup_date', todayStr);
    console.log('[Supabase Auto Backup] Auto backup complete.');
  } catch (err) {
    console.error('[Supabase Auto Backup] Failed:', err);
  }
}
